import { MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { subCategoryRepo } from "@repositories";
import { useQuery } from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { OrganizationBackgroundImagesGallery } from "./OrganizationBackgroundImagesGallery";
import type { BackgroundImageItem } from "./imageGalleryApi";

const byName = (a: { name?: string | null }, b: { name?: string | null }) =>
  (a?.name ?? "").localeCompare(b?.name ?? "", "he");

const text = (value?: string | null) => value ?? "";

const UNASSIGNED = "__unassigned__";
const ALL = "";
const ALL_SUBS = "__all__";

type SubCategoryOption = {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
};

type CategoryOption = {
  id: string;
  name: string;
  subs: SubCategoryOption[];
};

type Props = {
  organizationId: string | undefined;
  images: BackgroundImageItem[] | undefined;
  selectedImageId?: string;
  onSelectImage?: (imageId: string) => void;
  preferredSubCategoryId?: string;
  preferredCategoryIds?: string[];
};

export const BackgroundImageCatalog: FC<Props> = ({
  organizationId,
  images,
  selectedImageId,
  onSelectImage,
  preferredSubCategoryId,
  preferredCategoryIds,
}) => {
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState(ALL);
  const [subCategoryId, setSubCategoryId] = useState(ALL_SUBS);
  const appliedPreference = useRef(false);

  const { data: subCategories = [] } = useQuery({
    queryKey: ["organization-sub-categories", organizationId],
    queryFn: async () => {
      const rows = await subCategoryRepo.find({ include: { category: true } });
      return rows.map(
        (row): SubCategoryOption => ({
          id: row.id,
          name: text(row.name),
          categoryId: row.categoryId,
          categoryName: text(row.category?.name),
        }),
      );
    },
    enabled: Boolean(organizationId),
  });

  const categories = useMemo(() => {
    const byCategory = new Map<string, CategoryOption>();
    for (const sub of subCategories) {
      if (!sub.categoryId) continue;
      const category = byCategory.get(sub.categoryId) ?? {
        id: sub.categoryId,
        name: sub.categoryName,
        subs: [],
      };
      category.subs.push(sub);
      byCategory.set(sub.categoryId, category);
    }
    return [...byCategory.values()]
      .map((category) => ({
        ...category,
        subs: category.subs.slice().sort(byName),
      }))
      .sort(byName);
  }, [subCategories]);

  useEffect(() => {
    if (appliedPreference.current || categories.length === 0) return;
    appliedPreference.current = true;
    const preferredSub = subCategories.find(
      (sub) => sub.id === preferredSubCategoryId,
    );
    if (preferredSub) {
      setCategoryId(preferredSub.categoryId);
      setSubCategoryId(preferredSub.id);
      return;
    }
    const onlyCategoryId =
      preferredCategoryIds?.length === 1 ? preferredCategoryIds[0] : undefined;
    const preferredCategory = categories.find(
      (category) => category.id === onlyCategoryId,
    );
    if (preferredCategory) {
      setCategoryId(preferredCategory.id);
      setSubCategoryId(
        preferredCategory.subs.length === 1
          ? preferredCategory.subs[0]!.id
          : ALL_SUBS,
      );
      return;
    }
    setCategoryId(ALL);
    setSubCategoryId(ALL_SUBS);
  }, [categories, preferredCategoryIds, preferredSubCategoryId, subCategories]);

  const activeCategory = categories.find(
    (category) => category.id === categoryId,
  );
  const query = search.trim().toLocaleLowerCase("he");

  const visibleImages = useMemo(() => {
    const known = new Map(subCategories.map((sub) => [sub.id, sub]));
    return (images ?? []).filter((image) => {
      const linked = (image.subCategoryIds ?? [])
        .map((id) => known.get(id))
        .filter((sub): sub is SubCategoryOption => Boolean(sub));
      if (query) {
        const haystack = [
          ...linked.flatMap((sub) => [sub.name, sub.categoryName]),
          ...(linked.length === 0 ? ["ללא שיוך"] : []),
        ]
          .join(" ")
          .toLocaleLowerCase("he");
        return haystack.includes(query);
      }
      if (categoryId === UNASSIGNED) return linked.length === 0;
      if (!categoryId) return true;
      if (subCategoryId === ALL_SUBS) {
        return linked.some((sub) => sub.categoryId === categoryId);
      }
      return (image.subCategoryIds ?? []).includes(subCategoryId);
    });
  }, [categoryId, images, query, subCategories, subCategoryId]);

  return (
    <Stack gap={1.5} sx={{ width: "100%" }}>
      <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          value={search}
          placeholder="חיפוש"
          onChange={(event) => setSearch(event.target.value)}
          sx={{ width: 160 }}
        />
        <Select
          size="small"
          value={categoryId}
          onChange={(event) => {
            setSearch("");
            setCategoryId(event.target.value);
            setSubCategoryId(ALL_SUBS);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value={ALL}>כל הקטגוריות</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name || "קטגוריה"}
            </MenuItem>
          ))}
          <MenuItem value={UNASSIGNED}>ללא שיוך</MenuItem>
        </Select>
        {activeCategory && (
          <Select
            size="small"
            value={subCategoryId}
            onChange={(event) => {
              setSearch("");
              setSubCategoryId(event.target.value);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value={ALL_SUBS}>כל התת-קטגוריות</MenuItem>
            {activeCategory.subs.map((sub) => (
              <MenuItem key={sub.id} value={sub.id}>
                {sub.name || "תת-קטגוריה"}
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>
      {visibleImages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          אין תמונות להצגה.
        </Typography>
      ) : (
        <OrganizationBackgroundImagesGallery
          images={visibleImages}
          selectedImageId={selectedImageId}
          onSelectImage={onSelectImage}
        />
      )}
    </Stack>
  );
};
